/**
 * AURELIA — component-carousel.js
 * <product-carousel> — scroll-snap carousel controls.
 *
 * The track is a native scroller: touch, trackpad, momentum, keyboard and
 * screen-reader scrolling all work without this file. All it adds is the
 * previous/next buttons and the progress indicator, both driven by
 * `scrollBy` / `scrollLeft` so there is never a second source of truth about
 * where the carousel is.
 *
 * Works in RTL: distances are measured from the scroller's own metrics rather
 * than assuming left-to-right.
 */
(function () {
  'use strict';

  const { Element, utils } = window.Aurelia;

  class ProductCarousel extends Element {
    mount() {
      this.track = this.$('[data-carousel-track]');
      if (!this.track) return;

      this.previousButton = this.$('[data-carousel-prev]');
      this.nextButton = this.$('[data-carousel-next]');
      this.progress = this.$('[data-carousel-progress]');
      this.slides = this.$$('.carousel__slide');

      this.onScroll = utils.rafThrottle(() => this.sync());
      this.listen(this.track, 'scroll', this.onScroll, { passive: true });

      if (this.previousButton) {
        this.listen(this.previousButton, 'click', () => this.scrollByPage(-1));
      }
      if (this.nextButton) {
        this.listen(this.nextButton, 'click', () => this.scrollByPage(1));
      }

      // Keyboard focus moving into an off-screen slide must bring it into view;
      // the browser does this for `scrollIntoView` but not always for snap.
      this.listen(this.track, 'focusin', (event) => {
        const slide = event.target.closest('.carousel__slide');
        if (slide) slide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });

      this.resizeObserver = new ResizeObserver(() => this.sync());
      this.resizeObserver.observe(this.track);

      this.sync();
    }

    unmount() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /** Scroll distance for one "page" — the visible width, minus one gap. */
    get pageSize() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return this.track.clientWidth - gap;
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    scrollByPage(direction) {
      // `scrollLeft` is negative in RTL on standards-compliant browsers, so
      // the direction multiplier has to follow the document direction.
      const rtl = getComputedStyle(this).direction === 'rtl';
      const delta = this.pageSize * direction * (rtl ? -1 : 1);
      this.track.scrollBy({ left: delta, behavior: 'smooth' });
    }

    sync() {
      const max = this.maxScroll;
      const position = Math.abs(this.track.scrollLeft);

      // Nothing to scroll: hide the controls entirely rather than showing two
      // permanently disabled buttons.
      const isStatic = max <= 1;
      this.classList.toggle('is-static', isStatic);
      if (isStatic) return;

      // A 2px tolerance absorbs sub-pixel rounding at the ends.
      if (this.previousButton) this.previousButton.disabled = position <= 2;
      if (this.nextButton) this.nextButton.disabled = position >= max - 2;

      if (this.progress) {
        const visibleRatio = this.track.clientWidth / this.track.scrollWidth;
        const width = Math.max(visibleRatio * 100, 8);
        const offset = (position / max) * (100 - width);
        this.progress.style.setProperty('--progress-width', `${width}%`);
        this.progress.style.setProperty('--progress-offset', `${offset}%`);
      }
    }
  }

  window.Aurelia.define('product-carousel', ProductCarousel);
})();
